import { api } from './client';
import { ApiError } from './client';
import { testPoolKey, type ProbeResult } from './probe';
import type { PoolKey } from '../data/types';

/** getPoolKey reads ONE key, for a single-row refresh.
 *
 *  The whole point is that refreshing after a probe or a rename costs one
 *  document and replaces one row, instead of refetching the pool and handing
 *  React a new object for every row that did not change. */
export async function getPoolKey(id: string): Promise<PoolKey> {
  return api.get<PoolKey>(`/api/v1/admin/pool-keys/${encodeURIComponent(id)}`);
}

/** renamePoolKey changes a key's LABEL and nothing else.
 *
 *  The server accepts exactly one field and refuses any other, so a rename can
 *  never carry a credential by accident; replacing one is its own call. */
export async function renamePoolKey(id: string, label: string): Promise<PoolKey> {
  return api.patch<PoolKey>(`/api/v1/admin/pool-keys/${encodeURIComponent(id)}`, { label });
}

/** replacePoolKeyCredential replaces the secret behind a key (DR-78).
 *
 *  The text is whatever the operator pasted — a token-set JSON for a
 *  subscription entry, a key for an api_key entry — and the SERVER decides
 *  which it must be from the stored row's kind, so nothing sent here can turn
 *  one kind into the other. The response is the same projection every pool
 *  read returns: the new row, carrying no credential material. The old
 *  credential's rest, last error and quota snapshot are cleared with it. */
export async function replacePoolKeyCredential(id: string, credential: string): Promise<PoolKey> {
  return api.put<PoolKey>(`/api/v1/admin/pool-keys/${encodeURIComponent(id)}/credential`, {
    credential,
  });
}

/** setPoolKeyEnabled switches a key on or off (DR-99). Disabling is the
 *  reversible half of retiring a credential and the precondition for a
 *  delete; the response is the updated row. */
export async function setPoolKeyEnabled(id: string, enabled: boolean): Promise<PoolKey> {
  return api.patch<PoolKey>(`/api/v1/admin/pool-keys/${encodeURIComponent(id)}`, { enabled });
}

/** What the add dialog sends. kind decides what credential is: an API key
 *  the router seals before it stores it, or a subscription token set. */
export interface NewPoolKey {
  provider: string;
  kind: 'api_key' | 'oauth';
  label: string;
  scope: string;
  credential: string;
  enabled: boolean;
}

/** addPoolKey adds a credential to the pool (DR-99). The response is the new
 *  row — never the credential. */
export async function addPoolKey(input: NewPoolKey): Promise<PoolKey> {
  return api.post<PoolKey>('/api/v1/admin/pool-keys', input);
}

/** An API key for a provider that also pools subscriptions serves only as
 *  OVERFLOW (DR-99): when no subscription key is selectable, or for an API a
 *  subscription cannot serve (OpenAI embeddings). */
export function isOverflowKey(k: Pick<PoolKey, 'kind' | 'provider'>): boolean {
  return k.kind === 'api_key' && (k.provider === 'anthropic' || k.provider === 'openai');
}

/** What the edit dialog holds: the label as typed, the label it opened with,
 *  and whatever was pasted into the credential box. */
export interface KeyEdits {
  label: string;
  originalLabel: string;
  credential: string;
}

/** Which of the two writes the dialog's state actually asks for.
 *
 *  A label that differs only by surrounding whitespace is not a rename, and a
 *  credential box holding only whitespace is not a replacement — the server
 *  would refuse the second, but reporting "saved" for a request nobody made is
 *  the small lie that makes a console untrustworthy. */
export function pendingEdits(edits: KeyEdits): { rename: boolean; replace: boolean } {
  return {
    rename: edits.label.trim() !== edits.originalLabel.trim(),
    replace: edits.credential.trim() !== '',
  };
}

export interface KeyEditOutcome {
  /** The newest row the server returned, or null when nothing landed. */
  key: PoolKey | null;
  /** Which writes LANDED. Recorded here rather than recomputed by the caller:
   *  once the renamed row is upserted, the dialog's "original" label is the
   *  new one and a recomputation would say no rename was pending. */
  done: { rename: boolean; replace: boolean };
  /** The write that failed and what it said; absent when everything landed. */
  failed?: { stage: 'rename' | 'replace'; message: string };
}

/** saveKeyEdits applies the dialog's edits as the one or two requests they
 *  need, in order, stopping at the first failure.
 *
 *  Two requests rather than one, because the server keeps them apart on
 *  purpose (a rename can never carry a credential), and that leaves a partial
 *  outcome to report honestly: the rename runs first, so a replacement that
 *  fails leaves a renamed row on screen with an error that says exactly that,
 *  and a rename that fails stops before the credential is sent at all. The
 *  row that comes back is the latest the server returned, so the caller can
 *  upsert it whether or not the second write landed. */
export async function saveKeyEdits(
  id: string,
  edits: KeyEdits,
  ops: {
    rename: (id: string, label: string) => Promise<PoolKey>;
    replace: (id: string, credential: string) => Promise<PoolKey>;
  } = { rename: renamePoolKey, replace: replacePoolKeyCredential },
): Promise<KeyEditOutcome> {
  const pending = pendingEdits(edits);
  const done = { rename: false, replace: false };
  let key: PoolKey | null = null;
  if (pending.rename) {
    try {
      key = await ops.rename(id, edits.label.trim());
      done.rename = true;
    } catch (err) {
      return { key, done, failed: { stage: 'rename', message: describe(err) } };
    }
  }
  if (pending.replace) {
    try {
      key = await ops.replace(id, edits.credential.trim());
      done.replace = true;
    } catch (err) {
      return { key, done, failed: { stage: 'replace', message: describe(err) } };
    }
  }
  return { key, done };
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** deletePoolKey removes a credential from the pool permanently.
 *
 *  The only destructive call the console makes. The server refuses an ENABLED
 *  key with 409 and says so in its message — that refusal is the interlock, not
 *  a bug, and the message names the command that clears it. Everything else
 *  arrives as an ApiError the caller shows verbatim: a 404 means someone else
 *  already deleted it, and a 503 means this instance has no writable pool. */
export async function deletePoolKey(id: string): Promise<void> {
  await api.del<{ deleted: string }>(`/api/v1/admin/pool-keys/${encodeURIComponent(id)}`);
}

/** What the operator must type to confirm a delete.
 *
 *  A pool label is optional — the store trims it and does not require one — so
 *  matching against the label alone would arm the button before anything was
 *  typed whenever a key had no label, which is the opposite of a confirmation.
 *  The id is always present and always unique. */
export function confirmTarget(label: string | undefined, id: string): string {
  return (label ?? '').trim() || id;
}

/** What to CALL a key on screen.
 *
 *  The same fallback as confirmTarget and for the same reason: a label is
 *  optional, and renaming one to empty is allowed. Without this, clearing two
 *  labels leaves two blank rows an operator cannot tell apart — and the delete
 *  dialog would be asking them to confirm a name that is not displayed
 *  anywhere. */
export function poolKeyName(key: { label?: string; id: string }): string {
  return confirmTarget(key.label, key.id);
}

/** Whether the delete button may fire.
 *
 *  Four independent conditions, and each one is load-bearing:
 *
 *  - `live` — `/dev` mounts the same dialog over FIXTURE rows, but `api.del`
 *    would reach the real API as the real signed-in operator. A delete pressed
 *    against sample data would destroy a production credential.
 *  - `!enabled` — the server refuses an enabled key with 409; arming the button
 *    for a request that cannot succeed is a worse dead end than a disabled one,
 *    because the operator only learns after committing.
 *  - `!busy` — a second click on a request in flight would delete once and then
 *    report 404 for the same action.
 *  - the typed confirmation matches. */
export function canDelete(opts: {
  typed: string;
  target: string;
  enabled: boolean;
  live: boolean;
  busy: boolean;
}): boolean {
  // An empty target is never confirmable. `confirmTarget` cannot produce one
  // today, but a predicate that answers "yes, delete" to an empty box and an
  // empty target is one refactor away from being wrong, and the thing it would
  // be wrong about is destroying a credential.
  if (opts.target.trim() === '') return false;
  return opts.live && !opts.enabled && !opts.busy && opts.typed.trim() === opts.target;
}

/** What a retest ended up proving, and what the row looks like afterwards. */
export interface RetestOutcome {
  /** The probe's own verdict. Read it with `passed()`; a provider REFUSING the
   *  credential is a successful question with a negative answer, not a fault. */
  result: ProbeResult;
  /** The refreshed row, or null when the follow-up read did not land. */
  key: PoolKey | null;
  /** The key is gone — someone deleted it while the probe ran. */
  gone: boolean;
}

/** retestPoolKey probes one credential and re-reads just that row.
 *
 *  The refresh runs after a FAILED probe too, deliberately: a refusal moves
 *  failure counters and can rest or bench the key, so the row is at its most
 *  out-of-date exactly when the probe went badly.
 *
 *  A probe verdict is never lost to a refresh problem. The two questions are
 *  independent — "does this credential work" and "what does its record say now"
 *  — and reporting a successful probe as a failure because a follow-up GET timed
 *  out would be the more misleading of the two answers. */
export async function retestPoolKey(id: string): Promise<RetestOutcome> {
  // A transport failure here still leaves the row stale: the server may have
  // completed the probe and moved failure counters or rested the key before the
  // response was lost. So the refresh runs on EVERY path, and only the verdict
  // differs.
  let result: ProbeResult;
  try {
    result = await testPoolKey(id);
  } catch (err) {
    result = {
      poolKeyId: id,
      passed: false,
      outcome: 'did_not_complete',
      detail: err instanceof Error ? err.message : String(err),
    };
  }
  try {
    return { result, key: await getPoolKey(id), gone: false };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { result, key: null, gone: true };
    return { result, key: null, gone: false };
  }
}

/** Whether a key can be probed at all.
 *
 *  Every credential kind is probeable — that was the point of teaching the
 *  prober to unseal API keys — so this exists for the fixture harness alone:
 *  `/dev` rows are sample data while `api.post` would reach the real router as
 *  the real operator, and a probe SPENDS a shared subscription's quota. */
export function canRetest(opts: { live: boolean; busy: boolean }): boolean {
  return opts.live && !opts.busy;
}
